import React, { useEffect, useReducer, Component } from 'react';
import { SaveIcon, RotateCcwIcon, ShieldIcon, SlidersIcon, BellIcon, DatabaseIcon, CheckCircleIcon, AlertTriangleIcon, ShieldAlert, FileJson as FileJsonIcon, ScrollText as ScrollTextIcon } from 'lucide-react';
import { EnforcementSection } from './EnforcementSection';
import { SchemaEnforcementSection } from './SchemaEnforcementSection';
import { GraceLedgerSection } from './GraceLedgerSection';
import { Button } from '../../../../components/ui/Button';
import { Toggle as ToggleSwitch } from '../../../../components/ui/Toggle';
import { Input } from '../../../../components/ui/Input';
import { loadGovernanceConfig, saveGovernanceConfig, resetGovernanceConfig, getDefaultGovernanceConfig, type GovernanceConfigOverrides } from '../../../../governance';
// ─── Sub-components ───────────────────────────────────────────────────────────
interface ToggleProps {
  label: string;
  description: string;
  value: boolean;
  onChange: (v: boolean) => void;
}
function Toggle({
  label,
  description,
  value,
  onChange
}: ToggleProps) {
  return <div className="flex items-start justify-between gap-4 py-4 border-b border-gray-50 last:border-0" data-id="element-4295">
      <div className="min-w-0" data-id="element-4296">
        <p className="text-sm font-medium text-gray-800" data-id="element-4297">{label}</p>
        <p className="text-xs text-gray-500 mt-0.5" data-id="element-4298">{description}</p>
      </div>
      <ToggleSwitch checked={value} onChange={onChange} label={label} variant="admin" data-id="element-4299" />
    </div>;
}
interface SliderFieldProps {
  label: string;
  description: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  onChange: (v: number) => void;
  unit?: string;
  colorFn?: (v: number) => string;
}
function SliderField({
  label,
  description,
  value,
  min,
  max,
  step = 1,
  onChange,
  unit = '',
  colorFn
}: SliderFieldProps) {
  const color = colorFn ? colorFn(value) : 'text-blue-600';
  return <div className="py-4 border-b border-gray-50 last:border-0" data-id="element-4300">
      <div className="flex items-center justify-between mb-1" data-id="element-4301">
        <p className="text-sm font-medium text-gray-800" data-id="element-4302">{label}</p>
        <span className={`text-sm font-bold ${color}`} data-id="element-4303">
          {value}
          {unit}
        </span>
      </div>
      <p className="text-xs text-gray-500 mb-3" data-id="element-4304">{description}</p>
      <input type="range" min={min} max={max} step={step} value={value} onChange={e => onChange(Number(e.target.value))} className="w-full h-2 rounded-full appearance-none cursor-pointer bg-gray-200 accent-blue-500" data-id="element-4305" />
      <div className="flex justify-between mt-1" data-id="element-4306">
        <span className="text-xs text-gray-400" data-id="element-4307">
          {min}
          {unit}
        </span>
        <span className="text-xs text-gray-400" data-id="element-4308">
          {max}
          {unit}
        </span>
      </div>
    </div>;
}
interface NumberFieldProps {
  label: string;
  description: string;
  value: number;
  min?: number;
  max?: number;
  onChange: (v: number) => void;
  unit?: string;
}
function NumberField({
  label,
  description,
  value,
  min,
  max,
  onChange,
  unit
}: NumberFieldProps) {
  return <div className="py-4 border-b border-gray-50 last:border-0" data-id="element-4309">
      <div className="flex items-start justify-between gap-4" data-id="element-4310">
        <div className="min-w-0" data-id="element-4311">
          <p className="text-sm font-medium text-gray-800" data-id="element-4312">{label}</p>
          <p className="text-xs text-gray-500 mt-0.5" data-id="element-4313">{description}</p>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0" data-id="element-4314">
          <Input type="number" value={value} min={min} max={max} onChange={e => onChange(Number(e.target.value))} fullWidth={false} className="w-20 text-right" data-id="element-4315" />
          {unit && <span className="text-sm text-gray-500" data-id="element-4316">{unit}</span>}
        </div>
      </div>
    </div>;
}
// ─── Reducer ──────────────────────────────────────────────────────────────────
type SectionId = 'kwaliteit' | 'pipeline' | 'enforcement' | 'schema-enforcement' | 'grace' | 'beleid' | 'meldingen' | 'systeem';
interface SettingsState {
  activeSection: SectionId;
  saved: boolean;
  hasChanges: boolean;
  lastSaved?: string;
  // Quality gates
  minCogLevel: number;
  maxCogLevel: number;
  minDidacticDepth: number;
  minQualityScore: number;
  // Pipeline stages
  schemaEnabled: boolean;
  typeEnabled: boolean;
  contentEnabled: boolean;
  supportEnabled: boolean;
  visualEnabled: boolean;
  graphEnabled: boolean;
  pedagogyEnabled: boolean;
  // Pipeline behavior
  strictMode: boolean;
  autoBlock: boolean;
  // Policy
  minQuestionsPerSkill: number;
  maxQuestionsPerSkill: number;
  requireHints: boolean;
  requireExplanation: boolean;
  requireVisuals: boolean;
  distributionCheck: boolean;
  // Notifications
  notifyBlockers: boolean;
  notifyWarnings: boolean;
  notifyWeeklyReport: boolean;
  notifyNewRules: boolean;
  // System
  cacheEnabled: boolean;
  debugMode: boolean;
  autoValidateOnSave: boolean;
  batchSize: number;
}
type SettingsAction = {
  type: 'SET';
  field: keyof SettingsState;
  value: boolean | number | string;
} | {
  type: 'LOAD_CONFIG';
  config: GovernanceConfigOverrides;
} | {
  type: 'MARK_SAVED';
} | {
  type: 'CLEAR_SAVED';
} | {
  type: 'SET_SECTION';
  section: SectionId;
};
function configToState(c: GovernanceConfigOverrides): Omit<SettingsState, 'activeSection' | 'saved' | 'hasChanges'> {
  return {
    lastSaved: c.lastSaved,
    minCogLevel: c.qualityGates?.minCogLevel ?? 2,
    maxCogLevel: c.qualityGates?.maxCogLevel ?? 5,
    minDidacticDepth: c.qualityGates?.minDidacticDepth ?? 2,
    minQualityScore: c.qualityGates?.minQualityScore ?? 70,
    schemaEnabled: c.pipelineStages?.schemaEnabled ?? true,
    typeEnabled: c.pipelineStages?.typeEnabled ?? true,
    contentEnabled: c.pipelineStages?.contentEnabled ?? true,
    supportEnabled: c.pipelineStages?.supportEnabled ?? true,
    visualEnabled: c.pipelineStages?.visualEnabled ?? true,
    graphEnabled: c.pipelineStages?.graphEnabled ?? true,
    pedagogyEnabled: c.pipelineStages?.pedagogyEnabled ?? true,
    strictMode: c.pipelineBehavior?.strictMode ?? false,
    autoBlock: c.pipelineBehavior?.autoBlock ?? true,
    minQuestionsPerSkill: c.policy?.minQuestionsPerSkill ?? 5,
    maxQuestionsPerSkill: c.policy?.maxQuestionsPerSkill ?? 50,
    requireHints: c.policy?.requireHints ?? true,
    requireExplanation: c.policy?.requireExplanation ?? true,
    requireVisuals: c.policy?.requireVisuals ?? false,
    distributionCheck: c.policy?.distributionCheck ?? true,
    notifyBlockers: c.notifications?.notifyBlockers ?? true,
    notifyWarnings: c.notifications?.notifyWarnings ?? false,
    notifyWeeklyReport: c.notifications?.notifyWeeklyReport ?? true,
    notifyNewRules: c.notifications?.notifyNewRules ?? true,
    cacheEnabled: c.system?.cacheEnabled ?? true,
    debugMode: c.system?.debugMode ?? false,
    autoValidateOnSave: c.system?.autoValidateOnSave ?? true,
    batchSize: c.system?.batchSize ?? 100
  };
}
function stateToConfig(s: SettingsState): GovernanceConfigOverrides {
  return {
    version: 1,
    qualityGates: {
      minCogLevel: s.minCogLevel,
      maxCogLevel: s.maxCogLevel,
      minDidacticDepth: s.minDidacticDepth,
      minQualityScore: s.minQualityScore
    },
    pipelineStages: {
      schemaEnabled: s.schemaEnabled,
      typeEnabled: s.typeEnabled,
      contentEnabled: s.contentEnabled,
      supportEnabled: s.supportEnabled,
      visualEnabled: s.visualEnabled,
      graphEnabled: s.graphEnabled,
      pedagogyEnabled: s.pedagogyEnabled
    },
    pipelineBehavior: {
      strictMode: s.strictMode,
      autoBlock: s.autoBlock
    },
    policy: {
      minQuestionsPerSkill: s.minQuestionsPerSkill,
      maxQuestionsPerSkill: s.maxQuestionsPerSkill,
      requireHints: s.requireHints,
      requireExplanation: s.requireExplanation,
      requireVisuals: s.requireVisuals,
      distributionCheck: s.distributionCheck
    },
    notifications: {
      notifyBlockers: s.notifyBlockers,
      notifyWarnings: s.notifyWarnings,
      notifyWeeklyReport: s.notifyWeeklyReport,
      notifyNewRules: s.notifyNewRules
    },
    system: {
      cacheEnabled: s.cacheEnabled,
      debugMode: s.debugMode,
      autoValidateOnSave: s.autoValidateOnSave,
      batchSize: s.batchSize
    }
  };
}
function settingsReducer(state: SettingsState, action: SettingsAction): SettingsState {
  switch (action.type) {
    case 'SET':
      return {
        ...state,
        [action.field]: action.value,
        hasChanges: true,
        saved: false
      };
    case 'LOAD_CONFIG':
      return {
        ...state,
        ...configToState(action.config),
        hasChanges: false,
        saved: false
      };
    case 'MARK_SAVED':
      return {
        ...state,
        saved: true,
        hasChanges: false
      };
    case 'CLEAR_SAVED':
      return {
        ...state,
        saved: false
      };
    case 'SET_SECTION':
      return {
        ...state,
        activeSection: action.section
      };
    default:
      return state;
  }
}
function createInitialState(): SettingsState {
  const defaults = getDefaultGovernanceConfig();
  return {
    activeSection: 'kwaliteit',
    saved: false,
    hasChanges: false,
    ...configToState(defaults)
  };
}
// ─── Main Component ───────────────────────────────────────────────────────────
export function GovernanceSettingsTab() {
  const [state, dispatch] = useReducer(settingsReducer, undefined, createInitialState);
  // Helper to dispatch field changes
  const set = <K extends keyof SettingsState,>(field: K, value: SettingsState[K]) => dispatch({
    type: 'SET',
    field,
    value: value as boolean | number | string
  });
  // Load persisted config from dataLayer on mount
  useEffect(() => {
    loadGovernanceConfig().then(loaded => {
      dispatch({
        type: 'LOAD_CONFIG',
        config: loaded
      });
    }).catch(() => {});
  }, []);
  const handleSave = async () => {
    const newConfig = stateToConfig(state);
    const success = await saveGovernanceConfig(newConfig);
    if (success) {
      dispatch({
        type: 'MARK_SAVED'
      });
      setTimeout(() => dispatch({
        type: 'CLEAR_SAVED'
      }), 3000);
    }
  };
  const handleReset = async () => {
    const defaults = await resetGovernanceConfig();
    dispatch({
      type: 'LOAD_CONFIG',
      config: defaults
    });
  };
  const sections: {
    id: SectionId;
    label: string;
    icon: React.ReactNode;
  }[] = [{
    id: 'kwaliteit',
    label: 'Kwaliteitspoorten',
    icon: <ShieldIcon className="h-4 w-4" data-id="element-4317" />
  }, {
    id: 'pipeline',
    label: 'Pipeline',
    icon: <SlidersIcon className="h-4 w-4" data-id="element-4318" />
  }, {
    id: 'enforcement',
    label: 'Enforcement',
    icon: <ShieldAlert className="h-4 w-4" data-id="element-4319" />
  }, {
    id: 'schema-enforcement',
    label: 'Schema Ramp',
    icon: <FileJsonIcon className="h-4 w-4" data-id="element-4320" />
  }, {
    id: 'grace',
    label: 'Grace Ledger',
    icon: <ScrollTextIcon className="h-4 w-4" data-id="element-4321" />
  }, {
    id: 'beleid',
    label: 'Beleid',
    icon: <CheckCircleIcon className="h-4 w-4" data-id="element-4322" />
  }, {
    id: 'meldingen',
    label: 'Meldingen',
    icon: <BellIcon className="h-4 w-4" data-id="element-4323" />
  }, {
    id: 'systeem',
    label: 'Systeem',
    icon: <DatabaseIcon className="h-4 w-4" data-id="element-4324" />
  }];
  const cogLevelNames: Record<number, string> = {
    1: 'Onthouden',
    2: 'Begrijpen',
    3: 'Toepassen',
    4: 'Analyseren',
    5: 'Evalueren',
    6: 'Creëren'
  };
  const didacticNames: Record<number, string> = {
    1: 'Oppervlakkig',
    2: 'Procedureel',
    3: 'Conceptueel',
    4: 'Analytisch',
    5: 'Creatief'
  };
  return <div className="space-y-5" data-id="element-4325">
      <div className="flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3" data-id="element-4326">
        <CheckCircleIcon className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" data-id="element-4327" />
        <div data-id="element-4328">
          <p className="text-sm font-semibold text-emerald-800" data-id="element-4329">
            Instellingen worden lokaal opgeslagen
          </p>
          <p className="text-xs text-emerald-700 mt-0.5 leading-relaxed" data-id="element-4330">
            Wijzigingen worden opgeslagen in je browser en blijven behouden na
            het herladen van de pagina.
            {state.lastSaved && <span className="ml-1 text-emerald-600" data-id="element-4331">
                Laatst opgeslagen:{' '}
                {new Date(state.lastSaved).toLocaleString('nl-NL')}
              </span>}
          </p>
        </div>
      </div>

      {(state.hasChanges || state.saved) && <div className={`flex items-center justify-between rounded-xl border px-4 py-3 ${state.saved ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'}`} data-id="element-4332">
          <div className="flex items-center gap-2" data-id="element-4333">
            {state.saved ? <CheckCircleIcon className="h-4 w-4 text-emerald-600" data-id="element-4334" /> : <AlertTriangleIcon className="h-4 w-4 text-amber-600" data-id="element-4335" />}
            <p className={`text-sm font-medium ${state.saved ? 'text-emerald-700' : 'text-amber-700'}`} data-id="element-4336">
              {state.saved ? 'Instellingen opgeslagen!' : 'Je hebt niet-opgeslagen wijzigingen'}
            </p>
          </div>
          {!state.saved && <div className="flex gap-2" data-id="element-4337">
              <Button variant="ghost" size="sm" onClick={handleReset} data-id="element-4338">
                <RotateCcwIcon className="h-3.5 w-3.5 mr-1" data-id="element-4339" />
                Herstel
              </Button>
              <Button variant="primary" size="sm" onClick={handleSave} data-id="element-4340">
                <SaveIcon className="h-3.5 w-3.5 mr-1" data-id="element-4341" />
                Opslaan
              </Button>
            </div>}
        </div>}

      <div className="flex flex-col gap-5 lg:flex-row" data-id="element-4342">
        <div className="lg:w-48 flex-shrink-0" data-id="element-4343">
          <nav className="space-y-1" data-id="element-4344">
            {sections.map(section => <button key={section.id} onClick={() => dispatch({
            type: 'SET_SECTION',
            section: section.id
          })} className={`flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${state.activeSection === section.id ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-800'}`} data-id="element-4345">
                <span className={state.activeSection === section.id ? 'text-blue-600' : 'text-gray-400'} data-id="element-4346">
                  {section.icon}
                </span>
                {section.label}
              </button>)}
          </nav>
        </div>

        <div className="flex-1 min-w-0" data-id="element-4347">
          <div className="rounded-xl border border-gray-100 bg-white p-5" data-id="element-4348">
            {state.activeSection === 'kwaliteit' && <div data-id="element-4349">
                <h2 className="text-base font-bold text-gray-900 mb-1" data-id="element-4350">
                  Kwaliteitspoorten
                </h2>
                <p className="text-sm text-gray-500 mb-5" data-id="element-4351">
                  Stel de minimale kwaliteitseisen in waaraan vragen moeten
                  voldoen.
                </p>
                <SliderField label="Minimaal cognitief niveau" description={`Vragen onder niveau ${state.minCogLevel} (${cogLevelNames[state.minCogLevel]}) worden gemarkeerd`} value={state.minCogLevel} min={1} max={6} onChange={v => set('minCogLevel', v)} colorFn={v => v <= 2 ? 'text-amber-600' : 'text-blue-600'} data-id="element-4352" />
                <SliderField label="Maximaal cognitief niveau" description={`Vragen boven niveau ${state.maxCogLevel} (${cogLevelNames[state.maxCogLevel]}) worden gemarkeerd`} value={state.maxCogLevel} min={1} max={6} onChange={v => set('maxCogLevel', v)} colorFn={v => v >= 5 ? 'text-emerald-600' : 'text-blue-600'} data-id="element-4353" />
                <SliderField label="Minimale didactische diepte" description={`Vragen onder diepte ${state.minDidacticDepth} (${didacticNames[state.minDidacticDepth]}) worden gemarkeerd`} value={state.minDidacticDepth} min={1} max={5} onChange={v => set('minDidacticDepth', v)} data-id="element-4354" />
                <SliderField label="Minimale kwaliteitsscore" description="Vragen onder deze drempel worden als onvoldoende beschouwd" value={state.minQualityScore} min={0} max={100} step={5} onChange={v => set('minQualityScore', v)} unit="%" colorFn={v => v >= 80 ? 'text-emerald-600' : v >= 60 ? 'text-amber-600' : 'text-red-600'} data-id="element-4355" />
              </div>}

            {state.activeSection === 'pipeline' && <div data-id="element-4356">
                <h2 className="text-base font-bold text-gray-900 mb-1" data-id="element-4357">
                  Pipeline Instellingen
                </h2>
                <p className="text-sm text-gray-500 mb-5" data-id="element-4358">
                  Schakel validatiefasen in of uit en configureer het
                  pipeline-gedrag.
                </p>
                <Toggle label="Schema Validatie" description="Controleer JSON-structuur en verplichte velden" value={state.schemaEnabled} onChange={v => set('schemaEnabled', v)} data-id="element-4359" />
                <Toggle label="Vraagtype Validatie" description="Controleer vraagtype-specifieke regels" value={state.typeEnabled} onChange={v => set('typeEnabled', v)} data-id="element-4360" />
                <Toggle label="Inhoud & Taalvalidatie" description="Controleer didactische kwaliteit en taalgebruik" value={state.contentEnabled} onChange={v => set('contentEnabled', v)} data-id="element-4361" />
                <Toggle label="Hint & Uitleg Validatie" description="Controleer aanwezigheid van ondersteunend materiaal" value={state.supportEnabled} onChange={v => set('supportEnabled', v)} data-id="element-4362" />
                <Toggle label="Visuele Hulpmiddelen Validatie" description="Controleer visuele componenten en antwoordlekken" value={state.visualEnabled} onChange={v => set('visualEnabled', v)} data-id="element-4363" />
                <Toggle label="LDG-Graaf Validatie" description="Controleer leerpad-afhankelijkheden" value={state.graphEnabled} onChange={v => set('graphEnabled', v)} data-id="element-4364" />
                <Toggle label="Pedagogisch Contract" description="Controleer DOK-niveaus, misconceptie-tags en indicator-antwoord pariteit. Aan = stage runt; de strengheid per module (off/shadow/warn/enforce) stel je in onder Enforcement." value={state.pedagogyEnabled} onChange={v => set('pedagogyEnabled', v)} data-id="element-4365" />
                <div className="mt-4 pt-4 border-t border-gray-100" data-id="element-4366">
                  <Toggle label="Strikte modus" description="Behandel waarschuwingen als blokkerende fouten" value={state.strictMode} onChange={v => set('strictMode', v)} data-id="element-4367" />
                  <Toggle label="Automatisch blokkeren" description="Blokkeer vragen automatisch bij BLOCKER-fouten" value={state.autoBlock} onChange={v => set('autoBlock', v)} data-id="element-4368" />
                </div>
              </div>}

            {state.activeSection === 'enforcement' && <EnforcementSection data-id="element-4369" />}

            {state.activeSection === 'schema-enforcement' && <SchemaEnforcementSection data-id="element-4370" />}

            {state.activeSection === 'grace' && <GraceLedgerSection data-id="element-4371" />}

            {state.activeSection === 'beleid' && <div data-id="element-4372">
                <h2 className="text-base font-bold text-gray-900 mb-1" data-id="element-4373">
                  Beleidsregels
                </h2>
                <p className="text-sm text-gray-500 mb-5" data-id="element-4374">
                  Configureer de beleidsregels voor vraagsets en
                  vaardigheidsdekking.
                </p>
                <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 mb-5" data-id="element-4375">
                  <AlertTriangleIcon className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-4376" />
                  <p className="text-xs text-amber-700 leading-relaxed" data-id="element-4377">
                    Deze instellingen worden opgeslagen maar zijn nog{' '}
                    <strong data-id="element-4378">niet aangesloten op de validatie-pipeline</strong>{' '}
                    (open item GSR-14 / V-07). Wijzigingen hebben dus voorlopig
                    geen effect op welke vragen worden afgekeurd of gemarkeerd.
                  </p>
                </div>
                <NumberField label="Minimaal aantal vragen per vaardigheid" description="Vaardigheden met minder vragen krijgen een waarschuwing" value={state.minQuestionsPerSkill} min={1} max={20} onChange={v => set('minQuestionsPerSkill', v)} unit="vragen" data-id="element-4379" />
                <NumberField label="Maximaal aantal vragen per vaardigheid" description="Vaardigheden met meer vragen krijgen een informatieve melding" value={state.maxQuestionsPerSkill} min={10} max={200} onChange={v => set('maxQuestionsPerSkill', v)} unit="vragen" data-id="element-4380" />
                <Toggle label="Hints verplicht" description="Vragen zonder hint krijgen een waarschuwing" value={state.requireHints} onChange={v => set('requireHints', v)} data-id="element-4381" />
                <Toggle label="Uitleg verplicht" description="Vragen zonder uitleg krijgen een waarschuwing" value={state.requireExplanation} onChange={v => set('requireExplanation', v)} data-id="element-4382" />
                <Toggle label="Visuele hulpmiddelen verplicht" description="Vragen zonder visueel hulpmiddel krijgen een waarschuwing" value={state.requireVisuals} onChange={v => set('requireVisuals', v)} data-id="element-4383" />
                <Toggle label="Cognitief niveau distributie controleren" description="Controleer of vaardigheden een gevarieerde verdeling hebben" value={state.distributionCheck} onChange={v => set('distributionCheck', v)} data-id="element-4384" />
              </div>}

            {state.activeSection === 'meldingen' && <div data-id="element-4385">
                <h2 className="text-base font-bold text-gray-900 mb-1" data-id="element-4386">
                  Meldingsinstellingen
                </h2>
                <p className="text-sm text-gray-500 mb-5" data-id="element-4387">
                  Kies wanneer je meldingen wilt ontvangen over
                  governance-activiteit.
                </p>
                <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 mb-5" data-id="element-4388">
                  <AlertTriangleIcon className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-4389" />
                  <p className="text-xs text-amber-700 leading-relaxed" data-id="element-4390">
                    Er is nog <strong data-id="element-4391">geen meldingen-infrastructuur</strong> in
                    de app: deze voorkeuren worden opgeslagen maar er worden
                    momenteel geen meldingen verstuurd. Bewaard als
                    voorbereiding op een latere notificatielaag.
                  </p>
                </div>
                <Toggle label="Meldingen bij blokkerende fouten" description="Ontvang een melding wanneer een vraag wordt geblokkeerd" value={state.notifyBlockers} onChange={v => set('notifyBlockers', v)} data-id="element-4392" />
                <Toggle label="Meldingen bij waarschuwingen" description="Ontvang een melding bij elke nieuwe waarschuwing" value={state.notifyWarnings} onChange={v => set('notifyWarnings', v)} data-id="element-4393" />
                <Toggle label="Wekelijks kwaliteitsrapport" description="Ontvang elke week een samenvatting van de kwaliteitsmetingen" value={state.notifyWeeklyReport} onChange={v => set('notifyWeeklyReport', v)} data-id="element-4394" />
                <Toggle label="Meldingen bij nieuwe regels" description="Ontvang een melding wanneer nieuwe governance-regels worden toegevoegd" value={state.notifyNewRules} onChange={v => set('notifyNewRules', v)} data-id="element-4395" />
              </div>}

            {state.activeSection === 'systeem' && <div data-id="element-4396">
                <h2 className="text-base font-bold text-gray-900 mb-1" data-id="element-4397">
                  Systeeminstellingen
                </h2>
                <p className="text-sm text-gray-500 mb-5" data-id="element-4398">
                  Technische instellingen voor het governance-systeem.
                </p>
                <Toggle label="Cache inschakelen" description="Sla validatieresultaten op in cache voor snellere verwerking" value={state.cacheEnabled} onChange={v => set('cacheEnabled', v)} data-id="element-4399" />
                <Toggle label="Automatisch valideren bij opslaan" description="Voer validatie automatisch uit wanneer een vraag wordt opgeslagen" value={state.autoValidateOnSave} onChange={v => set('autoValidateOnSave', v)} data-id="element-4400" />
                <Toggle label="Foutopsporingsmodus" description="Toon uitgebreide foutinformatie in de console" value={state.debugMode} onChange={v => set('debugMode', v)} data-id="element-4401" />
                <NumberField label="Batch-grootte voor validatie" description="Aantal vragen dat tegelijk wordt gevalideerd" value={state.batchSize} min={10} max={500} onChange={v => set('batchSize', v)} unit="vragen" data-id="element-4402" />
                <div className="mt-6 pt-4 border-t border-gray-100" data-id="element-4403">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3" data-id="element-4404">
                    Gevaarlijke Acties
                  </h3>
                  <div className="space-y-2" data-id="element-4405">
                    <Button variant="secondary" size="sm" className="w-full justify-start opacity-50 cursor-not-allowed" disabled title="Binnenkort beschikbaar" data-id="element-4406">
                      <RotateCcwIcon className="h-4 w-4 mr-2" data-id="element-4407" />
                      Cache leegmaken
                    </Button>
                    <Button variant="danger" size="sm" className="w-full justify-start opacity-50 cursor-not-allowed" disabled title="Binnenkort beschikbaar" data-id="element-4408">
                      Alle instellingen terugzetten naar standaard
                    </Button>
                  </div>
                </div>
              </div>}
          </div>

          {/*
           * The Enforcement / Schema Ramp / Grace Ledger sections own their own
           * load/save lifecycle (separate from this reducer). Showing the global
           * footer there is misleading — its `state.hasChanges` never reflects
           * those sections, so the button stays permanently inactive and users
           * think "save is broken". Only render the global footer for sections
           * that are actually driven by this reducer.
           */}
          {state.activeSection !== 'enforcement' && state.activeSection !== 'schema-enforcement' && state.activeSection !== 'grace' && <div className="flex justify-end gap-3 mt-4" data-id="element-4409">
                <Button variant="ghost" size="sm" onClick={handleReset} disabled={!state.hasChanges} data-id="element-4410">
                  <RotateCcwIcon className="h-4 w-4 mr-1.5" data-id="element-4411" />
                  Herstel
                </Button>
                <Button variant="primary" size="sm" onClick={handleSave} disabled={!state.hasChanges} data-id="element-4412">
                  <SaveIcon className="h-4 w-4 mr-1.5" data-id="element-4413" />
                  Wijzigingen Opslaan
                </Button>
              </div>}
        </div>
      </div>
    </div>;
}